import { CaretRightIcon, MagnifyingGlassIcon, PlusIcon, TruckIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import diff from 'object-diff';
import { useMemo, useState } from 'react';

import { ITableHeader, Table } from '../../../components/table/Table';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { formatDate, formatRelativeToNow } from '../../../utils/date';
import { LinkButton } from '../../../components/button/ButtonLink';
import { Button } from 'components/button/Button';
import { EmptyState } from 'components/empty-state/EmptyState';
import { Input } from '../../../components/input/Input';
import { Pill } from '../../../components/pill/Pill';
import { InputField } from '../../../components/input/InputField';
import { useTranslation } from '../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { getDisplayError } from '@utils/get-display-error';
import { updateCarrierPortalCarrier } from '../models';
import { getTruckDocumentStatus, TRUCK_DOCUMENT_STATUS_COLOR } from './truck-document-status';

/** Expiry column: the date, plus how long is left once it needs attention. */
const ExpiryCell: React.FC<{ date: Date | null }> = ({ date }) => {
  const { lang, i18n } = useTranslation();
  const status = getTruckDocumentStatus(date);

  if (!date) {
    return <span className="text-fg-muted">{i18n('carrierPortal.trucks.statusMissing')}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-medium">{formatDate(date)}</span>
      {status !== 'valid' && <Pill color={TRUCK_DOCUMENT_STATUS_COLOR[status]}>{formatRelativeToNow(date, lang)}</Pill>}
    </div>
  );
};

export const CarrierPortalInfoTruckPage: React.FC = () => {
  const { supplier, carrierId } = useCarrierPortalSupplier();
  const { i18n } = useTranslation();
  const [search, setSearch] = useState('');

  const initialValues = useMemo(() => {
    return {
      truckAmount: `${supplier.truckAmount}`,
      tautlinerAmount: `${supplier.tautlinerAmount}`,
      boxTrailerAmount: `${supplier.boxTrailerAmount}`,
      openTrailerAmount: `${supplier.openTrailerAmount}`,
    };
  }, [supplier]);

  const TRUCK_HEADERS: ITableHeader[] = [
    { id: 'licensePlate', name: i18n('carrierPortal.trucks.licensePlate') },
    { id: 'insuranceExpiresAt', name: i18n('carrierPortal.trucks.insuranceExpiresAt'), width: '240px' },
    { id: 'examinationExpiresAt', name: i18n('carrierPortal.trucks.examinationExpiresAt'), width: '240px' },
    { id: 'documents', name: i18n('carrierPortal.trucks.documents'), width: '300px' },
    { id: 'chevron', name: '', width: '20px', align: 'right' },
  ];

  const trucks = useMemo(() => {
    const query = search.trim().toLowerCase();
    return supplier.trucks
      .filter((t) => !query || t.licensePlate.toLowerCase().includes(query))
      .sort((a, b) => a.sortIdx - b.sortIdx);
  }, [supplier.trucks, search]);

  /** Inspections needing attention drive the badge next to the section title. */
  const examinationsNeedingAttention = supplier.trucks.filter(
    (t) => getTruckDocumentStatus(t.examinationExpiresAt) !== 'valid',
  ).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="heading-two">{i18n('carrierPortal.trucks.title')}</h2>

        {examinationsNeedingAttention > 0 && (
          <Pill color="warning">
            {`${i18n('carrierPortal.trucks.examination')} · ${i18n('carrierPortal.trucks.statusExpiringSoon')}${
              examinationsNeedingAttention > 1 ? ` (${examinationsNeedingAttention})` : ''
            }`}
          </Pill>
        )}

        <div className="flex-1" />

        <div className="w-[280px] max-w-full">
          <Input
            type="text"
            value={search}
            onChange={setSearch}
            placeholder={i18n('carrierPortal.trucks.searchPlaceholder')}
            iconLeft={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
          />
        </div>

        <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
          {i18n('carrierPortal.trucks.newTruck')}
        </LinkButton>
      </div>

      <Table
        idKey="id"
        headers={TRUCK_HEADERS}
        data={trucks}
        rowHref={(truck) => truck.id}
        emptyState={<EmptyState icon={<TruckIcon />} title={i18n('carrierPortal.trucks.notFound')} />}
        mapData={(truck) => {
          const uploaded = [
            { type: 'Insurance', label: i18n('carrierPortal.trucks.insurance') },
            { type: 'Examination', label: i18n('carrierPortal.trucks.examination') },
          ].filter((d) => truck.documents.some((doc) => doc.type === d.type));
          const missingCount = 2 - uploaded.length;

          return [
            <span className="font-semibold">{truck.licensePlate}</span>,
            <ExpiryCell date={truck.insuranceExpiresAt} />,
            <ExpiryCell date={truck.examinationExpiresAt} />,
            <div className="flex flex-wrap items-center gap-1.5">
              {uploaded.map((d) => (
                <Pill color="positive" key={d.type}>
                  {d.label}
                </Pill>
              ))}
              {missingCount > 0 && (
                <Pill color="neutral">
                  {`${i18n('carrierPortal.trucks.statusMissing')}${missingCount > 1 ? ` (${missingCount})` : ''}`}
                </Pill>
              )}
            </div>,
            <CaretRightIcon className="h-3.5 w-3.5 text-fg-muted" />,
          ];
        }}
      />

      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="heading-two">{i18n('carrierPortal.trailers.title')}</h2>
        <span className="text-xs text-fg-muted">{i18n('carrierPortal.trailers.subtitle')}</span>
      </div>

      <div className="card p-5">
        <Formik
          initialValues={initialValues}
          onSubmit={async (newValues) => {
            try {
              const patch: Partial<typeof newValues> = diff(initialValues, newValues);
              const updateData: {
                truckAmount?: number;
                tautlinerAmount?: number;
                boxTrailerAmount?: number;
                openTrailerAmount?: number;
              } = {};

              if (patch.truckAmount !== undefined) updateData.truckAmount = +patch.truckAmount;
              if (patch.tautlinerAmount !== undefined) updateData.tautlinerAmount = +patch.tautlinerAmount;
              if (patch.boxTrailerAmount !== undefined) updateData.boxTrailerAmount = +patch.boxTrailerAmount;
              if (patch.openTrailerAmount !== undefined) updateData.openTrailerAmount = +patch.openTrailerAmount;

              if (Object.keys(updateData).length) {
                await updateCarrierPortalCarrier(carrierId, updateData);
              }
              toast.success(i18n('carrierPortal.trailers.saveSuccess'));
            } catch (err: any) {
              captureException(err);
              toast.error(i18n('carrierPortal.trailers.saveError', { error: getDisplayError(err) }));
            }
          }}
        >
          {({ handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-3.5">
                {(
                  [
                    ['truckAmount', 'carrierPortal.trailers.truckAmount'],
                    ['tautlinerAmount', 'carrierPortal.trailers.tautlinerAmount'],
                    ['boxTrailerAmount', 'carrierPortal.trailers.boxTrailerAmount'],
                    ['openTrailerAmount', 'carrierPortal.trailers.openTrailerAmount'],
                  ] as const
                ).map(([name, labelKey]) => (
                  <div className="w-[200px]" key={name}>
                    <InputField labelText={i18n(labelKey)} type="number" name={name} />
                  </div>
                ))}
              </div>

              <div>
                <Button type="submit" color="secondary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                  {i18n('carrierPortal.home.saveChanges')}
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};
